import React, { useState, useEffect, useRef } from "react";
import {
    Box, Typography, Avatar, List, ListItem, ListItemAvatar,
    ListItemText, Divider, InputBase, IconButton, styled, Paper, Menu, MenuItem, CircularProgress
} from "@mui/material";
import {
    Search as SearchIcon, MoreVert as MoreVertIcon, Send as SendIcon,
    DoneAll as DoneAllIcon, Security as SecurityIcon, SupportAgent as SupportAgentIcon
} from "@mui/icons-material";

// Firebase Imports
import { db } from "../firebase";
import {
    collection, addDoc, query, where, orderBy, getDoc, getDocs,
    onSnapshot, serverTimestamp, doc, writeBatch, limit, updateDoc
} from "firebase/firestore";

// --- STYLED COMPONENTS ---
const SidebarContainer = styled(Box)({
    width: 380,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid #d1d7db",
    height: "100%",
});

const ChatWindow = styled(Box)({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#efeae2",
    backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
    height: "100%",
});

const ScrollableArea = styled(Box)({
    flex: 1,
    overflowY: "auto",
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#ced0d1' },
});

const MessageBubbleContainer = styled(Box)(({ isTeacher }) => ({
    display: "flex",
    width: "100%",
    justifyContent: isTeacher ? "flex-start" : "flex-end",
    marginBottom: 4,
    "&:hover .msg-menu-btn": { opacity: 1 }
}));

const MessageBubble = styled(Paper)(({ isTeacher, isDeleted }) => ({
    padding: "8px 12px 4px 12px",
    maxWidth: "100%",
    borderRadius: 8,
    backgroundColor: isDeleted ? "#ffffff" : (isTeacher ? "#ffffff" : "#dcf8c6"),
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    position: "relative",
    borderTopRightRadius: isTeacher ? 8 : 0,
    borderTopLeftRadius: isTeacher ? 0 : 8,
    fontStyle: isDeleted ? "italic" : "normal",
    color: isDeleted ? "#8696a0" : "inherit",
}));

// --- COMPONENT: STUDENT LIST ITEM IN SIDEBAR ---
const StudentListItem = ({ student, isSelected, onSelect }) => {
    return (
        <React.Fragment>
            <ListItem
                button
                onClick={() => onSelect(student)}
                selected={isSelected}
                sx={{
                    py: 1.5,
                    px: 2,
                    "&.Mui-selected": { bgcolor: "#f0f2f5" },
                    "&:hover": { bgcolor: "#f5f6f6" }
                }}
            >
                <ListItemAvatar sx={{ minWidth: 50 }}>
                    <Avatar sx={{ bgcolor: "#00a884", width: 42, height: 42, fontSize: 16 }}>
                        {student.firstname?.charAt(0) || "S"}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: student.unreadCount > 0 ? 700 : 600, fontSize: '0.88rem', color: "#111b21" }}>
                                {student.fullName || "Loading..."} ({student.className || "..."})
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: student.unreadCount > 0 ? "#25D366" : "#667781", fontWeight: student.unreadCount > 0 ? 600 : 400 }}>
                                {student.lastTime || ""}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box sx={{ mt: 0.2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.1 }}>
                                <Typography variant="caption" noWrap sx={{
                                    maxWidth: '85%',
                                    fontSize: '0.78rem',
                                    color: student.unreadCount > 0 ? "#111b21" : "#667781",
                                    fontWeight: student.unreadCount > 0 ? 600 : 400
                                }}>
                                    {student.lastMsg || "No messages yet"}
                                </Typography>
                                {student.unreadCount > 0 && (
                                    <Box sx={{
                                        bgcolor: '#25D366', color: '#fff', borderRadius: '50%',
                                        width: 18, height: 18, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 'bold'
                                    }}>
                                        {student.unreadCount}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    }
                />
            </ListItem>
            <Divider variant="inset" component="li" sx={{ borderColor: "#f2f2f2", ml: "70px" }} />
        </React.Fragment>
    );
};

// --- MAIN CONTROLLER COMPONENT ---
export default function TeacherStudentChatMonitor() {
    const [allTeachers, setAllTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState(null);

    const [studentsWithHistory, setStudentsWithHistory] = useState([]);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);

    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [input, setInput] = useState("");

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [activeTargetMsg, setActiveTargetMsg] = useState(null);

    const chatEndRef = useRef(null);
    const subscriptionsRef = useRef({});

    // 1. Fetch ALL Teachers for top horizontal bar
    useEffect(() => {
        const teacherQuery = query(collection(db, "users"), where("role", "==", "Teacher"));
        const unsubscribeTeachers = onSnapshot(teacherQuery, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllTeachers(list);
            if (list.length > 0 && !selectedTeacher) {
                setSelectedTeacher(list[0]); // Auto-select first teacher
            }
        });
        return () => unsubscribeTeachers();
    }, []);

    // Clean active listeners
    const clearSubscriptions = () => {
        Object.values(subscriptionsRef.current).forEach(unsub => {
            if (typeof unsub === "function") unsub();
        });
        subscriptionsRef.current = {};
    };

    // 2. When a Teacher is selected, fetch all Students and check for existing Teacher <-> Student chats
    useEffect(() => {
        if (!selectedTeacher) return;

        setLoadingStudents(true);
        setSelectedStudent(null);
        setMessages([]);
        clearSubscriptions();

        const fetchStudentsAndChats = async () => {
            try {
                const studentsSnap = await getDocs(collection(db, "students"));
                const rawStudents = studentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const structuralStudents = await Promise.all(rawStudents.map(async (student) => {
                    let resolvedClass = "Not Admitted";
                    const fullName = `${student.firstname || ""} ${student.fathername || ""} ${student.surname || ""}`.trim();

                    try {
                        const admQuery = query(
                            collection(db, "admission"),
                            where("studentId", "==", student.id),
                            where("status", "==", "Active"),
                            limit(1)
                        );
                        const admSnap = await getDocs(admQuery);
                        if (!admSnap.empty) {
                            const admData = admSnap.docs[0].data();
                            const classId = admData.classId || admData.class;
                            if (classId) {
                                const courseSnap = await getDoc(doc(db, "courses", classId));
                                resolvedClass = courseSnap.exists() ? courseSnap.data().name : classId;
                            }
                        }
                    } catch (err) {
                        console.error("Class resolution error:", err);
                    }

                    return {
                        ...student,
                        fullName,
                        studentId: student.id,
                        className: resolvedClass,
                        lastMsg: "",
                        lastTime: "",
                        lastActiveTime: 0,
                        unreadCount: 0,
                        hasHistory: false
                    };
                }));

                setStudentsWithHistory(structuralStudents);
                setLoadingStudents(false);

                // Check chat history specifically between current selectedTeacher and each student
                structuralStudents.forEach(student => {
                    if (subscriptionsRef.current[student.id]) return;

                    const chatId = selectedTeacher.id < student.id
                        ? `${selectedTeacher.id}_${student.id}`
                        : `${student.id}_${selectedTeacher.id}`;

                    const msgQuery = query(collection(db, "chats", chatId, "messages"), orderBy("createdAt", "desc"));

                    subscriptionsRef.current[student.id] = onSnapshot(msgQuery, (msgSnapshot) => {
                        setStudentsWithHistory(prevStudents => {
                            return prevStudents.map(oldStudent => {
                                if (oldStudent.id !== student.id) return oldStudent;

                                if (!msgSnapshot.empty) {
                                    const latestDoc = msgSnapshot.docs[0].data();
                                    const time = latestDoc.createdAt?.toDate();

                                    const unreadCount = msgSnapshot.docs.filter(docSnap => {
                                        const d = docSnap.data();
                                        return d.senderId !== selectedTeacher.id && d.status !== "read" && !d.isDeleted;
                                    }).length;

                                    return {
                                        ...oldStudent,
                                        hasHistory: true,
                                        lastMsg: latestDoc.isDeleted ? "🚫 This message was deleted" : (latestDoc.text || "Media message"),
                                        lastTime: time ? formatLastMessageTime(time) : "",
                                        lastActiveTime: time ? time.getTime() : 0,
                                        unreadCount: unreadCount
                                    };
                                } else {
                                    return { ...oldStudent, hasHistory: false };
                                }
                            });
                        });
                    });
                });
            } catch (error) {
                console.error("Error fetching students:", error);
                setLoadingStudents(false);
            }
        };

        fetchStudentsAndChats();

        return () => {
            clearSubscriptions();
        };
    }, [selectedTeacher?.id]);

    // 3. Load actual Messages stream between selectedTeacher and selectedStudent
    useEffect(() => {
        if (!selectedTeacher || !selectedStudent) return;

        setLoadingMessages(true);
        const chatId = selectedTeacher.id < selectedStudent.id
            ? `${selectedTeacher.id}_${selectedStudent.id}`
            : `${selectedStudent.id}_${selectedTeacher.id}`;

        const q = query(collection(db, "chats", chatId, "messages"), orderBy("createdAt", "asc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })));
            setLoadingMessages(false);

            const batch = writeBatch(db);
            let hasUpdates = false;
            snapshot.docs.forEach((msgDoc) => {
                const data = msgDoc.data();
                if (data.senderId !== selectedTeacher.id && data.status !== "read" && !data.isDeleted) {
                    batch.update(doc(db, "chats", chatId, "messages", msgDoc.id), { status: "read", readAt: serverTimestamp() });
                    hasUpdates = true;
                }
            });
            if (hasUpdates) batch.commit();
        });
        return unsubscribe;
    }, [selectedTeacher?.id, selectedStudent?.id]);

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    // Send message as teacher proxy
    const handleSend = async () => {
        if (!input.trim() || !selectedTeacher || !selectedStudent) return;
        const chatId = selectedTeacher.id < selectedStudent.id
            ? `${selectedTeacher.id}_${selectedStudent.id}`
            : `${selectedStudent.id}_${selectedTeacher.id}`;
        const text = input.trim();
        setInput("");
        try {
            await addDoc(collection(db, "chats", chatId, "messages"), {
                text,
                senderId: selectedTeacher.id,
                receiverId: selectedStudent.id,
                createdAt: serverTimestamp(),
                status: "sent",
                isDeleted: false
            });
        } catch (e) { console.error("Error sending message: ", e); }
    };

    const handleOpenMenu = (event, msg) => {
        setMenuAnchor(event.currentTarget);
        setActiveTargetMsg(msg);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setActiveTargetMsg(null);
    };

    const handleDeleteForEveryone = async () => {
        if (!activeTargetMsg || !selectedTeacher || !selectedStudent) return;
        const chatId = selectedTeacher.id < selectedStudent.id
            ? `${selectedTeacher.id}_${selectedStudent.id}`
            : `${selectedStudent.id}_${selectedTeacher.id}`;
        try {
            await updateDoc(doc(db, "chats", chatId, "messages", activeTargetMsg.id), {
                text: "🚫 This message was deleted",
                isDeleted: true,
                status: "deleted"
            });
        } catch (e) { console.error("Failed to delete message: ", e); }
        handleCloseMenu();
    };

    const handleDeleteForMe = () => {
        if (activeTargetMsg) {
            setMessages(prev => prev.filter(m => m.id !== activeTargetMsg.id));
        }
        handleCloseMenu();
    };

    // Filter students who have active conversation history with current selected teacher
    const filteredStudents = studentsWithHistory
        .filter(s => s.hasHistory)
        .filter(s => {
            const search = searchQuery.toLowerCase();
            return (
                s.fullName?.toLowerCase().includes(search) ||
                s.className?.toLowerCase().includes(search) ||
                s.firstname?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => b.lastActiveTime - a.lastActiveTime);

    const formatLastMessageTime = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();

        if (
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear()
        ) {
            return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
        }

        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);

        if (
            date.getDate() === yesterday.getDate() &&
            date.getMonth() === yesterday.getMonth() &&
            date.getFullYear() === yesterday.getFullYear()
        ) {
            return "Yesterday";
        }

        const diffDays = Math.floor((now.setHours(0,0,0,0) - date.setHours(0,0,0,0)) / (1000 * 60 * 60 * 24));
        if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;

        return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    };

    return (
        <Box sx={{ display: "flex", height: "85vh", bgcolor: "#f0f2f5" }}>
            <Paper elevation={4} sx={{ display: "flex", flex: 1, overflow: "hidden", borderRadius: 2 }}>

                {/* SIDEBAR PANEL */}
                <SidebarContainer>
                    {/* TOP TEACHERS SELECTION ROW */}
                    <Box sx={{ p: 2, bgcolor: "#fff", pb: 1, borderBottom: "1px solid #e0e0e0" }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: "#008069" }}>
                            Teacher-Student Monitor
                        </Typography>

                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#667781", textTransform: "uppercase", letterSpacing: 0.5 }}>
                            Select Teacher
                        </Typography>

                        <Box sx={{
                            display: "flex",
                            gap: 1.5,
                            overflowX: "auto",
                            py: 1.5,
                            px: 0.5,
                            '&::-webkit-scrollbar': { height: '4px' },
                            '&::-webkit-scrollbar-thumb': { backgroundColor: '#ced0d1', borderRadius: 2 }
                        }}>
                            {allTeachers.length === 0 ? (
                                <Typography variant="caption" sx={{ color: "#8696a0" }}>No teachers found</Typography>
                            ) : (
                                allTeachers.map((teacher) => {
                                    const isSelected = selectedTeacher?.id === teacher.id;
                                    return (
                                        <Box
                                            key={teacher.id}
                                            onClick={() => setSelectedTeacher(teacher)}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                cursor: "pointer",
                                                minWidth: 55,
                                                maxWidth: 60
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    bgcolor: isSelected ? "#0288d1" : "#b0bec5",
                                                    width: 44,
                                                    height: 44,
                                                    boxShadow: isSelected ? "0 0 0 3px #0288d1" : "none",
                                                    transition: "all 0.2s"
                                                }}
                                            >
                                                {teacher.name?.charAt(0) || "T"}
                                            </Avatar>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    mt: 0.5,
                                                    fontWeight: isSelected ? 700 : 500,
                                                    color: isSelected ? "#0288d1" : "#111b21",
                                                    textAlign: "center",
                                                    width: "100%"
                                                }}
                                            >
                                                {teacher.name || teacher.email?.split("@")[0] || "Teacher"}
                                            </Typography>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>

                    {/* STUDENT CHATS LIST */}
                    <Box sx={{ p: 2, bgcolor: "#fff", pb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#667781", textTransform: "uppercase", mb: 1, display: "block" }}>
                            Students chatting with {selectedTeacher?.name || "Teacher"}
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f0f2f5", borderRadius: 2, px: 2, height: 36 }}>
                            <SearchIcon sx={{ color: "gray", fontSize: 18 }} />
                            <InputBase
                                placeholder="Search student name / class..."
                                sx={{ ml: 1, flex: 1, fontSize: 13 }}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <ScrollableArea>
                        {loadingStudents ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 4, flexDirection: "column", gap: 1 }}>
                                <CircularProgress size={28} sx={{ color: "#00a884" }} />
                                <Typography variant="caption" sx={{ color: "#667781" }}>Syncing conversations...</Typography>
                            </Box>
                        ) : filteredStudents.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" sx={{ color: "#667781" }}>
                                    No active student chats for {selectedTeacher?.name || "this teacher"}.
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {filteredStudents.map(student => (
                                    <StudentListItem
                                        key={student.id}
                                        student={student}
                                        isSelected={selectedStudent?.id === student.id}
                                        onSelect={setSelectedStudent}
                                    />
                                ))}
                            </List>
                        )}
                    </ScrollableArea>
                </SidebarContainer>

                {/* MAIN CHAT DISPLAY AREA */}
                <ChatWindow>
                    {selectedTeacher && selectedStudent ? (
                        <>
                            {/* Header */}
                            <Box sx={{ p: "10px 16px", bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid #e0e0e0" }}>
                                <Avatar sx={{ bgcolor: "#00a884" }}>
                                    {selectedStudent.fullName?.charAt(0) || "S"}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {selectedStudent.fullName} ({selectedStudent.className})
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#008069", fontWeight: 700 }}>
                                        Chat between Student & Teacher {selectedTeacher.name}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Message Area */}
                            <ScrollableArea sx={{ p: 3, display: "flex", flexDirection: "column" }}>
                                {loadingMessages ? (
                                    <Box sx={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center", my: "auto" }}>
                                        <CircularProgress sx={{ color: "#00a884" }} />
                                    </Box>
                                ) : (
                                    messages.map((msg) => {
                                        const isTeacher = msg.senderId === selectedTeacher.id;
                                        return (
                                            <MessageBubbleContainer key={msg.id} isTeacher={isTeacher}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%', flexDirection: isTeacher ? 'row-reverse' : 'row', gap: 0.5 }}>
                                                    {!msg.isDeleted && (
                                                        <IconButton
                                                            className="msg-menu-btn"
                                                            size="small"
                                                            onClick={(e) => handleOpenMenu(e, msg)}
                                                            sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0.2, color: '#8696a0' }}
                                                        >
                                                            <MoreVertIcon fontSize="inherit" />
                                                        </IconButton>
                                                    )}
                                                    <MessageBubble isTeacher={isTeacher} isDeleted={msg.isDeleted}>
                                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: isTeacher ? "#0288d1" : "#008069", mb: 0.3 }}>
                                                            {isTeacher ? `Teacher (${selectedTeacher.name})` : `Student (${selectedStudent.fullName})`}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 14.5, wordBreak: "break-word" }}>{msg.text}</Typography>
                                                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 0.2, gap: 0.5 }}>
                                                            <Typography sx={{ fontSize: 9, color: "#667781" }}>
                                                                {msg.createdAt?.toDate()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || "..."}
                                                            </Typography>
                                                            {isTeacher && !msg.isDeleted && (
                                                                <DoneAllIcon sx={{ fontSize: 15, ml: 0.2, color: msg.status === "read" ? "#53bdeb" : "#8696a0" }} />
                                                            )}
                                                        </Box>
                                                    </MessageBubble>
                                                </Box>
                                            </MessageBubbleContainer>
                                        );
                                    })
                                )}
                                <div ref={chatEndRef} />
                            </ScrollableArea>

                            {/* Dropdown Options */}
                            <Menu
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor)}
                                onClose={handleCloseMenu}
                            >
                                <MenuItem onClick={handleDeleteForMe} sx={{ fontSize: 13.5 }}>Delete for me</MenuItem>
                                {activeTargetMsg && activeTargetMsg.senderId === selectedTeacher.id && (
                                    <MenuItem onClick={handleDeleteForEveryone} sx={{ fontSize: 13.5, color: 'error.main' }}>
                                        Delete for everyone
                                    </MenuItem>
                                )}
                            </Menu>

                            {/* Response Bar */}
                            <Box sx={{ p: 2, bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 1.5 }}>
                                <InputBase
                                    fullWidth
                                    placeholder={`Reply on behalf of ${selectedTeacher.name}...`}
                                    sx={{ bgcolor: "#fff", borderRadius: 3, px: 2, py: 1, fontSize: 14, border: "1px solid #e0e0e0" }}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                />
                                <IconButton onClick={handleSend} sx={{ bgcolor: "#00a884", color: "#fff", "&:hover": { bgcolor: "#008069" } }}>
                                    <SendIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)" }}>
                            <Box sx={{ textAlign: 'center', p: 5 }}>
                                <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                                    <Box sx={{ width: 140, height: 140, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                                        <SupportAgentIcon sx={{ fontSize: 70, color: '#00a884' }} />
                                    </Box>
                                    <Box sx={{ position: 'absolute', bottom: 10, right: 0, bgcolor: '#25D366', p: 1, borderRadius: '50%', border: '4px solid #f8f9fa' }}>
                                        <SecurityIcon sx={{ color: '#fff', fontSize: 18 }} />
                                    </Box>
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#111b21', mb: 1 }}>Teacher-Student Chat View</Typography>
                                <Typography variant="body1" sx={{ color: '#667781', maxWidth: 380, mx: 'auto', lineHeight: 1.6 }}>
                                    Select a teacher at the top, then choose a student from the sidebar to inspect their direct chat history.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </ChatWindow>
            </Paper>
        </Box>
    );
}